import cx from 'classnames';
import { useEffect, useState, type ReactElement, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { Button, Icon } from '@sb/design-system';

import { CHAT_PANEL_CARD_ID } from './chatPanelConstants';
import type { PendingConfirmation } from './ChatPanelProvider';

export type ConfirmationDecision = 'approve' | 'reject';

interface ConfirmationModalProps {
  /**
   * Latest unresolved confirmation snapshot (from the `/events` stream's
   * `confirmation_request` payload). Null when no confirmation is
   * pending — the modal stays closed.
   */
  pending: PendingConfirmation | null;
  /**
   * Fires when the user picks a decision. The chat mount wires this
   * to `useResolveConfirmation.resolve(toolUseID, decision)`, which
   * POSTs `/confirm` and clears the pending state.
   */
  onResolve: (decision: ConfirmationDecision) => void;
  /**
   * Disables both buttons while the resolution is in flight. Prevents
   * a second tap from re-firing onResolve.
   */
  isResolving?: boolean;
  /**
   * Whether the chat panel itself is open. The DOM-side signal
   * (portal target lookup) lags the panel's exit animation — the
   * overlay keeps the card mounted ~200ms after close and its
   * delayed unmount never re-renders this sibling, leaving the
   * cached target pointing at a detached node. The provider's
   * `isOpen` flips synchronously on minimize, so gating on it hides
   * the modal — and disarms Escape-to-reject — the moment the panel
   * starts closing.
   */
  panelIsOpen: boolean;
}

/**
 * Resolve the panel-card portal target fresh after every commit. The
 * card element is destroyed and recreated each time the panel is
 * minimized and reopened, so caching the lookup (the old `useMemo`
 * keyed on `active`) left the portal pointing at a detached node —
 * a still-pending confirmation would never reappear after reopen.
 * The same-value bail keeps the every-render effect from looping.
 */
function usePanelPortalTarget(active: boolean): HTMLElement | null {
  const [target, setTarget] = useState<HTMLElement | null>(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally dependency-less: the card element is destroyed/recreated on minimize with no reactive signal, so the lookup must re-run after every commit; keying on [active] would cache the detached node (the bug this replaces). The same-value bail prevents update loops.
  useEffect(() => {
    if (typeof document === 'undefined') return;

    const next = active ? document.getElementById(CHAT_PANEL_CARD_ID) : null;

    setTarget((prev) => (prev === next ? prev : next));
  });

  return target;
}

function renderModalIntoPanel(
  target: HTMLElement | null,
  node: ReactNode,
): ReactElement | null {
  if (target === null) return null;

  return createPortal(node, target);
}

/**
 * Tool-use approval prompt triggered by the orchestrator's
 * `confirmation_request` event. Distinct from the in-bubble action
 * cards on assistant messages — those are paste-and-send shortcuts.
 * This prompt blocks the conversation until the user resolves the
 * pending tool call.
 *
 * Portalled into the chat panel card (via `CHAT_PANEL_CARD_ID`) so it
 * dims and overlays only the panel itself, matching the in-panel
 * feedback modal rather than a full-screen design-system Modal.
 */
export function ConfirmationModal({
  pending,
  onResolve,
  isResolving = false,
  panelIsOpen,
}: ConfirmationModalProps): ReactElement | null {
  const isOpen = pending !== null && panelIsOpen;
  const portalTarget = usePanelPortalTarget(isOpen);

  // Escape resolves as a reject so the user is never trapped if the
  // prompt body is empty / the buttons are obscured. Matches the
  // feedback modal's Escape semantics. Gated on the panel being open
  // (via `isOpen`) AND the portal target so a minimized panel — even
  // during the overlay's exit animation, when the card is briefly
  // still in the DOM or the cached target is a detached node — can't
  // reject invisibly.
  useEffect(() => {
    if (!isOpen) return undefined;
    if (isResolving) return undefined;
    if (portalTarget === null) return undefined;

    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onResolve('reject');
      }
    };

    window.addEventListener('keydown', handler);

    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, isResolving, onResolve, portalTarget]);

  if (!isOpen) return null;

  return renderModalIntoPanel(
    portalTarget,
    <div
      // `position: absolute; inset: 0` fills the chat panel card via
      // the portal target. Backdrop dims the panel only, not the
      // surrounding page.
      className={cx(
        'tw-absolute',
        'tw-inset-0',
        'tw-flex',
        'tw-items-center',
        'tw-justify-center',
        'tw-p-20',
      )}
      style={{
        zIndex: 100,
        background: 'rgba(0,0,0,0.45)',
        animation: '_chatBackdropIn 0.18s ease forwards',
      }}
      onClick={(e) => {
        // Swallow the click so it doesn't bubble through the React
        // portal tree to ChatPanelOverlay's backdrop and dismiss the
        // whole panel. Dismissal of the prompt itself is not allowed
        // via outside-click — the user must Approve, Reject, or hit
        // Escape (which routes to reject).
        e.stopPropagation();
      }}
      role="presentation"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Tool use confirmation"
        className={cx(
          'tw-w-full',
          'tw-max-w-320',
          'tw-rounded-14',
          'tw-p-16',
          'tw-flex',
          'tw-flex-col',
          'tw-gap-12',
        )}
        style={{
          background: 'var(--surface-primary-elevated)',
          boxShadow: '0 20px 50px rgba(0,0,0,0.25)',
        }}
      >
        <div className="tw-flex tw-items-center tw-gap-10">
          <div
            className={cx(
              'tw-flex',
              'tw-items-center',
              'tw-justify-center',
              'tw-w-32',
              'tw-h-32',
              'tw-rounded-full',
              'tw-shrink-0',
            )}
            style={{ background: 'rgba(234, 179, 8, 0.15)' }}
          >
            <Icon
              kind="exclamationTriangle"
              className="tw-w-16 tw-h-16"
              style={{ color: 'var(--mango)' }}
            />
          </div>
          <div
            style={{
              // 14/600 + `-0.2` letter-spacing aren't in the typography scale.
              fontSize: 14,
              fontWeight: 600,
              color: 'var(--label-primary)',
              letterSpacing: -0.2,
            }}
          >
            Approve this action?
          </div>
        </div>
        <p
          className={cx(
            'tw-m-0',
            'tw-text-13',
            'tw-text-label-secondary',
            'tw-whitespace-pre-wrap',
          )}
        >
          {pending?.prompt ?? ''}
        </p>
        <div className={cx('tw-flex', 'tw-justify-end', 'tw-gap-8')}>
          <Button
            type="button"
            variant="Gray"
            size={32}
            onClick={() => onResolve('reject')}
            disabled={isResolving}
          >
            Reject
          </Button>
          <Button
            type="button"
            variant="Filled"
            color="Orange"
            size={32}
            onClick={() => onResolve('approve')}
            disabled={isResolving}
          >
            Approve
          </Button>
        </div>
      </div>
    </div>,
  );
}
